/**
 * Khung dialog tạo/sửa: tiêu đề, nội dung cuộn, chân Hủy/Gửi.
 * Hủy hỏi xác nhận; nút X và Esc đóng thẳng; backdrop không đóng.
 */
import { useId, type ReactNode } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { attemptFormDialogClose, useConfirm } from '@/shared/confirm';
import Dialog, { DialogContent, DialogTitle, type DialogProps } from './Dialog';
import FormDialogFooter, { type FormDialogFooterProps } from './FormDialogFooter';
import IconButton from '../primitives/IconButton';
import { blockDialogCloseWhenBusy } from './formDialogChrome';
import { mergeSx } from '../utils/mergeSx';

const DEFAULT_MIN_HEIGHT = 360;

type FooterProps = Pick<
  FormDialogFooterProps,
  | 'onSubmit'
  | 'submitLabel'
  | 'busySubmitLabel'
  | 'submitDisabled'
  | 'submitIntent'
  | 'cancelVariant'
  | 'cancelLabel'
  | 'cancelDisabled'
  | 'submitType'
  | 'hideSubmit'
  | 'hideCancel'
  | 'startSlot'
  | 'between'
>;

export type FormDialogProps = FooterProps & {
  open: boolean;
  title: ReactNode;
  busy?: boolean;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: DialogProps['maxWidth'];
  /** Paper min height so the dialog does not grow after content loads. */
  minHeight?: number | string;
  contentSx?: SxProps<Theme>;
  paperSx?: SxProps<Theme>;
  /** Omit the footer while the dialog is loading or the record is missing. */
  hideFooter?: boolean;
};

const FormDialog = ({
  open,
  title,
  busy = false,
  onClose,
  children,
  maxWidth = 'sm',
  minHeight = DEFAULT_MIN_HEIGHT,
  contentSx,
  paperSx,
  onSubmit,
  submitLabel,
  busySubmitLabel,
  submitDisabled,
  submitIntent,
  cancelVariant = 'text',
  cancelLabel,
  cancelDisabled,
  submitType,
  hideSubmit,
  hideCancel,
  startSlot,
  between,
  hideFooter = false,
}: FormDialogProps) => {
  const { t } = useTranslation('common');
  const confirm = useConfirm();
  const titleId = useId();

  const dismiss = (): void => {
    if (busy) return;
    onClose();
  };

  const requestCancel = (): void => {
    void attemptFormDialogClose({
      busy,
      isDirty: true,
      confirm,
      onClose,
    });
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={maxWidth}
      scroll="paper"
      aria-labelledby={titleId}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        blockDialogCloseWhenBusy(busy, dismiss)?.();
      }}
      slotProps={{
        paper: {
          sx: mergeSx(
            {
              display: 'flex',
              flexDirection: 'column',
              minHeight,
              maxHeight: '90vh',
            },
            paperSx,
          ),
        },
      }}
    >
      <DialogTitle
        id={titleId}
        sx={{
          position: 'relative',
          pr: 6,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {title}
        <IconButton
          type="button"
          aria-label={t('close')}
          onClick={dismiss}
          disabled={busy}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={mergeSx(
          {
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            '&&': { pt: 2, pb: 2.5 },
          },
          contentSx,
        )}
      >
        {children}
      </DialogContent>
      {hideFooter ? null : (
        <FormDialogFooter
          busy={busy}
          cancelVariant={cancelVariant}
          cancelLabel={cancelLabel}
          cancelDisabled={cancelDisabled}
          onCancel={requestCancel}
          onSubmit={onSubmit}
          submitType={submitType}
          submitLabel={submitLabel}
          busySubmitLabel={busySubmitLabel}
          submitDisabled={submitDisabled}
          submitIntent={submitIntent}
          hideSubmit={hideSubmit}
          hideCancel={hideCancel}
          startSlot={startSlot}
          between={between}
        />
      )}
    </Dialog>
  );
};

export default FormDialog;
